import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  Keyboard,
  Platform,
  Pressable,
  Animated as RNAnimated,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native"

import { useRouter } from "expo-router"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import {
  CLUSTER_LINE_HEIGHT,
  CLUSTER_TEXT_AREA_DEFAULT,
  CompositeInputCluster,
} from "src/components/CompositeInputCluster"
import { MediaPlayerShell } from "src/components/MediaPlayerShell"
import { SidekickProgress } from "src/components/SidekickChat/SidekickProgress"
import { useOptionalAvatarMode } from "src/context/AvatarModeContext"
import { useOptionalMediaAvatar } from "src/context/MediaAvatarContext"
import { useSidekickInteraction } from "src/context/SidekickInteractionContext"
import type { ControlsConfig, SidekickViewProps } from "./types"
import { AvatarMode, AvatarOverlay, useAvatarVisibility } from "./AvatarOverlay"

/**
 * SidekickView - The authenticated sidekick layout shell for media-viewing
 * experiences.
 *
 * Composes MediaPlayerShell (presentation-only layout) with sidekick-specific
 * pieces: AvatarOverlay, CompositeInputCluster, and SidekickProgress.
 *
 * The CompositeInputCluster is a unified bottom bar that integrates text input,
 * microphone, and menu — replacing the previous separate ButtonCluster +
 * TypingChatInputDock combination.
 *
 * Control Configurations:
 * - "none": No extra controls, just CompositeInputCluster (for menu screens)
 * - "playback": Play/pause + seek slider (for video/audio players)
 * - "navigation": Back/forward buttons (for web browser)
 */
export function SidekickView({
  children,
  showAvatar = true,
  autoShowAvatarOnSpeech = true,
  showCaptions = true,
  showCCToggle = true,
  showButtonCluster = true,
  compactButtonCluster = false,
  controlsOpacity: externalControlsOpacity,
  controlsScale,
  controlsCollapsed = false,
  onExpandControls,
  onMenuPress,
  controlsConfig,
  style,
  controlsVisible = true,
  onContentPress,
  darkBackground = true,
  showControlsBarBackground = true,
  showControlsGradient = true,
  onModeChange,
  riveOwner = "video",
  useEnhancedAvatar = true,
  initialAvatarMode = "large",
  onAvatarModeChange,
  disableCCforView = false,
  avatarBackdrop = false,
  showProgressOverlay = true,
  leftAccessory: _leftAccessory,
  modeToggle,
}: SidekickViewProps) {
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const isLandscape = screenWidth > screenHeight
  const mediaAvatarContext = useOptionalMediaAvatar()

  const [controlsHeight, setControlsHeight] = useState(0)
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  useEffect(() => {
    const showSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow",
      (e) => {
        setIsKeyboardVisible(true)
        setKeyboardHeight(e.endCoordinates.height)
      },
    )
    const hideSubscription = Keyboard.addListener(
      Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide",
      () => {
        setIsKeyboardVisible(false)
        setKeyboardHeight(0)
      },
    )
    return () => {
      showSubscription.remove()
      hideSubscription.remove()
    }
  }, [])

  const handleMenuPress = useCallback(() => {
    if (onMenuPress) {
      onMenuPress()
    } else {
      router.push("/menu" as any)
    }
  }, [onMenuPress, router])

  const internalControlsOpacity = useRef(new RNAnimated.Value(1)).current
  const controlsOpacity = externalControlsOpacity ?? internalControlsOpacity

  const { inputMode, returnToIdle } = useSidekickInteraction()

  // When the host hides its controls while the user is typing (e.g. they tap
  // Play on the video player), dismiss the keyboard and exit type mode so the
  // text input doesn't remain active offscreen.
  useEffect(() => {
    if (!controlsVisible && inputMode === "type") {
      Keyboard.dismiss()
      returnToIdle()
    }
  }, [controlsVisible, inputMode, returnToIdle])

  // Reuse MediaPlayerShell's existing tap-capture overlay (rendered only when
  // onContentPress is provided) — no new overlay needed. When the keyboard is
  // open, intercept the tap to dismiss it; otherwise fall through to the
  // caller's handler. The overlay is only activated when one of those two
  // conditions is true, so normal content taps are never accidentally blocked.
  const handleContentPress = useCallback(() => {
    if (inputMode === "type") {
      Keyboard.dismiss()
      returnToIdle()
      return
    }
    onContentPress?.()
  }, [inputMode, returnToIdle, onContentPress])

  const effectiveContentPress =
    inputMode === "type" || onContentPress ? handleContentPress : undefined

  // Sync avatar mode with context - use context mode if available, fallback to prop
  const avatarModeContext = useOptionalAvatarMode()
  const avatarMode = avatarModeContext?.mode ?? initialAvatarMode

  const { shouldShowAvatar } = useAvatarVisibility()

  const handleAvatarModeChange = useCallback(
    (mode: AvatarMode) => {
      onAvatarModeChange?.(mode)
    },
    [onAvatarModeChange],
  )

  // Notify parent when mode changes (for backward compatibility)
  useEffect(() => {
    if (inputMode === "talk" || inputMode === "type") {
      onModeChange?.(inputMode)

      // If in a media route and user starts interaction, restore avatar to remembered size
      if (mediaAvatarContext?.isInMediaRoute && mediaAvatarContext.onInteractionStart) {
        mediaAvatarContext.onInteractionStart()
      }
    } else if (inputMode === "idle") {
      onModeChange?.("idle")
    }
  }, [inputMode, onModeChange, mediaAvatarContext])

  const effectiveConfig: ControlsConfig = useMemo(
    () => controlsConfig ?? { type: "none" },
    [controlsConfig],
  )

  // ── Avatar slot ────────────────────────────────────────────────────────────
  const avatarSlot = useMemo(() => {
    if (!showAvatar || !useEnhancedAvatar) return null
    if (!autoShowAvatarOnSpeech && !shouldShowAvatar) return null

    return (
      <AvatarOverlay
        showCaptions={showCaptions}
        onToggleCaptions={showCCToggle ? () => {} : undefined}
        riveOwner={riveOwner}
        onModeChange={handleAvatarModeChange}
        useFlexLayout={avatarMode === "large"}
        bottomOffset={
          isKeyboardVisible ? keyboardHeight + (controlsHeight || 80) : controlsHeight || 80
        }
        isLandscape={isLandscape}
        disableCCforView={disableCCforView}
        keyboardHeight={0}
      />
    )
  }, [
    showAvatar,
    useEnhancedAvatar,
    autoShowAvatarOnSpeech,
    shouldShowAvatar,
    showCaptions,
    showCCToggle,
    riveOwner,
    handleAvatarModeChange,
    avatarMode,
    isKeyboardVisible,
    keyboardHeight,
    controlsHeight,
    isLandscape,
    disableCCforView,
  ])

  // ── Avatar mode coordination ───────────────────────────────────────────────
  // When the cluster's text area expands (user typing many lines), step the
  // avatar down — large → minimized → tab — to avoid the avatar overlapping
  // the growing input. On collapse, restore the user's remembered size.
  const handleTextAreaHeightChange = useCallback(
    (height: number) => {
      const ctx = avatarModeContext
      if (!ctx) return

      if (height <= CLUSTER_TEXT_AREA_DEFAULT) {
        // Back to default — restore the user's last manual avatar size.
        const remembered = ctx.getRememberedSize()
        if (ctx.mode !== remembered) {
          ctx.setMode(remembered, false)
        }
        return
      }

      if (Platform.OS !== "web") {
        // Mobile: jump straight to tab on any expansion to maximise input space.
        if (ctx.mode !== "tab") {
          ctx.setMode("tab", false)
        }
      } else {
        // Web: step through minimize → tab based on expansion amount.
        const extra = height - CLUSTER_TEXT_AREA_DEFAULT
        const extraLines = extra / CLUSTER_LINE_HEIGHT
        if (extraLines < 4) {
          if (ctx.mode === "large") {
            ctx.setMode("minimized", false)
          }
        } else {
          if (ctx.mode !== "tab") {
            ctx.setMode("tab", false)
          }
        }
      }
    },
    [avatarModeContext],
  )

  // ── Bottom slot ────────────────────────────────────────────────────────────
  // The cluster fills the full bottom row — it now contains the modeToggle and
  // media controls internally, so no leftAccessory wrapper is needed here.
  const bottomSlot = useMemo(() => {
    if (!showButtonCluster) return null

    return (
      <View style={styles.bottomRow}>
        {/* Inner wrapper carries the uniform scale (Safari-style collapse). The
            transform does not affect layout, so styles.bottomRow keeps the
            cluster's original footprint for the tap-capture overlay below. */}
        <RNAnimated.View
          style={[
            styles.clusterScale,
            controlsScale != null && { transform: [{ scale: controlsScale }] },
          ]}
        >
          <CompositeInputCluster
            onMenuPress={handleMenuPress}
            modeToggle={modeToggle}
            controlsConfig={effectiveConfig}
            onTextAreaHeightChange={handleTextAreaHeightChange}
          />
        </RNAnimated.View>

        {/* Tap-to-expand: when the cluster is scrolled away (shrunken), a
            full-size transparent overlay restores it. Only mounted while
            collapsed so normal cluster interaction is unobstructed otherwise.
            Parent-wrapper opacity does not affect hit-testing, so this stays
            tappable even when the cluster has faded to ~0 opacity. */}
        {controlsCollapsed && onExpandControls && (
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={onExpandControls}
            accessibilityLabel="Expand input bar"
          />
        )}
      </View>
    )
  }, [
    showButtonCluster,
    handleMenuPress,
    modeToggle,
    effectiveConfig,
    handleTextAreaHeightChange,
    controlsScale,
    controlsCollapsed,
    onExpandControls,
  ])

  // ── Progress overlay ───────────────────────────────────────────────────────
  const progressOverlay = useMemo(() => {
    if (!showProgressOverlay) return null
    return <SidekickProgress variant="overlay" />
  }, [showProgressOverlay])

  const avatarBackdropVisible = avatarBackdrop && avatarMode === "large"

  const controlsExtraBottomPadding =
    compactButtonCluster && Platform.OS === "android" && insets.bottom === 0 ? 24 : 0

  // The controls bar background is the reserved bottom padding behind the
  // cluster. Screens that disable it (showControlsBarBackground=false) want the
  // cluster to float over edge-to-edge content at all times — including while
  // typing — so the reserve must follow showControlsBarBackground exactly and
  // not be re-enabled by "type" mode. This keeps it consistent with the
  // showControlsGradient gate and the documented contract on the prop.
  const reserveSpaceForControls = showControlsBarBackground

  // Pass leftAccessory as null to MediaPlayerShell — it is now included
  // inside the bottomSlot so the cluster can use flex: 1.
  // suppressBuiltInLeftControls still lets playback/navigation scrubbers render.
  return (
    <MediaPlayerShell
      style={style}
      darkBackground={darkBackground}
      reserveSpaceForControls={reserveSpaceForControls}
      controlsOpacity={controlsOpacity}
      controlsVisible={controlsVisible}
      onContentPress={effectiveContentPress}
      controlsConfig={effectiveConfig}
      leftAccessory={null}
      // Built-in play/pause and navigation buttons have moved into the cluster's
      // button row — suppress them here to avoid duplication.
      suppressBuiltInLeftControls={true}
      bottomSlot={bottomSlot}
      avatarSlot={avatarSlot}
      progressOverlay={progressOverlay}
      avatarBackdrop={avatarBackdrop}
      avatarBackdropVisible={avatarBackdropVisible}
      controlsExtraBottomPadding={controlsExtraBottomPadding}
      onControlsHeightChange={setControlsHeight}
      keepControlsOnKeyboard={showButtonCluster}
      // The gradient is part of the controls bar background, so it must follow
      // the same contract: no effect when showControlsBarBackground is false.
      // Without this gate, entering "type" mode flips reserveSpaceForControls on
      // and the gradient would flash in on screens (e.g. web-browser) that
      // explicitly disabled the bar background.
      showControlsGradient={showControlsGradient && showControlsBarBackground}
    >
      {children}
    </MediaPlayerShell>
  )
}

const styles = StyleSheet.create({
  // Wrapper row for the cluster. alignSelf: stretch fills the width provided
  // by MediaPlayerShell's bottomSlotWrapper (alignItems: stretch) without
  // claiming vertical flex space in that column container.
  bottomRow: {
    alignSelf: "stretch",
    flexDirection: "row",
    alignItems: "flex-end",
  },
  // Inner wrapper that receives the collapse scale. flex: 1 so it fills the
  // bottomRow width and the cluster keeps its normal layout before scaling.
  clusterScale: {
    flex: 1,
    flexDirection: "row",
    alignItems: "flex-end",
  },
})
